var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { CONFIG } from "../config/app.config";
import { EEnvironment } from "../config/app.config.types";
import { EBrokerFeature } from "../types/broker.types";
import { ELeaseType, ERegion } from "../types/contract.types";
import { ELocale } from "../types/i18n.types";
import { EPropertyEPCLabel, EPropertyMemberTypes, } from "../types/property.types";
import { getLocale, getLocalizedText } from "./i18n/i18n.utils";
var NOW = new Date();
var getTenantProfileDomain = function () {
    var locale = getLocale();
    if ([EEnvironment.Dev, EEnvironment.Stag, EEnvironment.Demo].includes(CONFIG.ENVIRONMENT)) {
        switch (locale) {
            case ELocale.NL:
                return "mijnhuurprofiel-internal.be";
            case ELocale.FR:
                return "monprofillocataire-internal.be";
            default:
                return "mytenantprofile-internal.be";
        }
    }
    switch (locale) {
        case ELocale.NL:
            return "mijnhuurprofiel.be";
        case ELocale.FR:
            return "monprofillocataire.be";
        default:
            return "mytenantprofile.be";
    }
};
var getTenantProfileEnvSubDomain = function () {
    var env = CONFIG.ENVIRONMENT;
    switch (env) {
        case EEnvironment.Dev:
            return "dev";
        case EEnvironment.Demo:
            return "demo";
        case EEnvironment.Stag:
            return "stag";
        case EEnvironment.Prod:
        default:
            return undefined;
    }
};
var getTenantProfileSubDomainItems = function (broker) {
    var items = [];
    if (CONFIG.ENVIRONMENT === EEnvironment.Prod) {
        if (broker === null || broker === void 0 ? void 0 : broker.intakePrefix) {
            items.push(broker.intakePrefix);
        }
        else if (broker === null || broker === void 0 ? void 0 : broker.maxImmoBrokerId) {
            items.push(broker.maxImmoBrokerId);
        }
    }
    var envSubDomain = getTenantProfileEnvSubDomain();
    if (envSubDomain) {
        items.push(envSubDomain);
    }
    return items;
};
var getRentMatchDomain = function () {
    if ([EEnvironment.Dev, EEnvironment.Stag, EEnvironment.Demo].includes(CONFIG.ENVIRONMENT)) {
        return "rentmatch-internal.be";
    }
    return "rentmatch.be";
};
export var getTenantProfileUrl = function (property, broker) {
    var _a, _b;
    if (!property.propertyInfoId) {
        return;
    }
    var hasRentMatch = (_a = broker === null || broker === void 0 ? void 0 : broker.features) === null || _a === void 0 ? void 0 : _a.includes(EBrokerFeature.RentMatch);
    // When running locally, point to the local tenant-profile-v2 dev server
    var isLocal = typeof window !== "undefined" && ((_b = window.location) === null || _b === void 0 ? void 0 : _b.hostname) === "localhost";
    if (isLocal) {
        var suffix = hasRentMatch
            ? "/property/".concat(property.propertyInfoId, "/rentmatch/intro")
            : "/property/".concat(property.propertyInfoId);
        return "http://localhost:3007".concat(suffix);
    }
    if (hasRentMatch) {
        var subDomainItems_1 = getTenantProfileSubDomainItems(broker);
        var domain_1 = getRentMatchDomain();
        var host_1 = __spreadArray(__spreadArray([], subDomainItems_1, true), [domain_1], false).join(".");
        // Use /rentmatch/intro so candidates land on the calculator/onboarding page,
        // not the authenticated /property route (which redirects to login).
        return "https://".concat(host_1, "/property/").concat(property.propertyInfoId, "/rentmatch/intro");
    }
    var domain = getTenantProfileDomain();
    var subDomainItems = getTenantProfileSubDomainItems(broker);
    var host = __spreadArray(__spreadArray([], subDomainItems, true), [domain], false).join(".");
    return "https://".concat(host, "/property/").concat(property.propertyInfoId);
};
// cspell:ignore bezoek plannen profiel aanvragen
export var getTenantBookingUrl = function (property, broker) {
    var _a, _b;
    if (!property.propertyInfoId) {
        return;
    }
    var hasRentMatch = (_a = broker === null || broker === void 0 ? void 0 : broker.features) === null || _a === void 0 ? void 0 : _a.includes(EBrokerFeature.RentMatch);
    // When running locally, point to the local tenant-profile-v2 dev server
    var isLocal = typeof window !== "undefined" && ((_b = window.location) === null || _b === void 0 ? void 0 : _b.hostname) === "localhost";
    if (isLocal) {
        var suffix = hasRentMatch
            ? "/property/".concat(property.propertyInfoId, "/bezoek-plannen")
            : "/profiel/aanvragen?openBooking=".concat(property.propertyInfoId);
        return "http://localhost:3007".concat(suffix);
    }
    if (hasRentMatch) {
        var subDomainItems_2 = getTenantProfileSubDomainItems(broker);
        var domain_2 = getRentMatchDomain();
        var host_2 = __spreadArray(__spreadArray([], subDomainItems_2, true), [domain_2], false).join(".");
        // RentMatch deep-links the booking modal via /property/:id/bezoek-plannen,
        // which redirects to /profiel/aanvragen?openBooking= (see rentmatch App.jsx).
        return "https://".concat(host_2, "/property/").concat(property.propertyInfoId, "/bezoek-plannen");
    }
    var domain = getTenantProfileDomain();
    var subDomainItems = getTenantProfileSubDomainItems(broker);
    var host = __spreadArray(__spreadArray([], subDomainItems, true), [domain], false).join(".");
    return "https://".concat(host, "/profiel/aanvragen?openBooking=").concat(property.propertyInfoId);
};
// The single place a Belgian region is derived. Every region rule in the app -
// the rental-deposit cap, Brussels registration, the EPC indexation rules, the
// EPC label list - reads it from here, so the ranges cannot drift apart.
// The upper bound of every range is exclusive: [1300, 1500] matches 1300-1499.
var BELGIUM_COUNTRY_ID = "BE";
var REGION_ZIP_RANGES = [
    [ERegion.Brussels, [[1000, 1300]]],
    [
        ERegion.Wallonia,
        [
            [1300, 1500],
            [4000, 8000],
        ],
    ],
    [
        ERegion.Flanders,
        [
            [1500, 4000],
            [8000, 10000],
        ],
    ],
];
// A property outside Belgium, or one whose country was never recorded, has no
// region: the ranges above describe Belgian postal codes only, and a Dutch
// "4000" is not Liege. Callers decide what an unknown region means for them.
export var getPropertyRegion = function (property) {
    var _a;
    if (!(property === null || property === void 0 ? void 0 : property.zip) || property.countryId !== BELGIUM_COUNTRY_ID) {
        return undefined;
    }
    var zipAsNumber = +property.zip;
    if (!Number.isFinite(zipAsNumber)) {
        return undefined;
    }
    return (_a = REGION_ZIP_RANGES.find(function (_a) {
        var ranges = _a[1];
        return ranges.some(function (_a) {
            var from = _a[0], to = _a[1];
            return zipAsNumber >= from && zipAsNumber < to;
        });
    })) === null || _a === void 0 ? void 0 : _a[0];
};
export var isFlemishProperty = function (property) {
    return getPropertyRegion(property) === ERegion.Flanders;
};
export var isBrusselsProperty = function (property) {
    return getPropertyRegion(property) === ERegion.Brussels;
};
export var isWalloonProperty = function (property) {
    return getPropertyRegion(property) === ERegion.Wallonia;
};
export var PROPERTY_BRUSSELS_ZIP_FILTER = {
    // Match Brussels zip ranges 1000 - 1299
    // zip: { regexp: "^12[0-9]{2}$|^11[0-9]{2}$|^10[0-9]{2}$" },
    and: [{ zip: { gte: 1000 } }, { zip: { lte: 1299 } }],
};
export var CONTRACT_BRUSSELS_LEASE_TYPE_FILTER = {
    leaseType: {
        in: [ELeaseType.MainResidence, ELeaseType.StudentHousing],
    },
    startDate: { gte: CONFIG.BRUSSELS_REGISTRATION_START_DATE },
};
export var getAvailableEpcLabelsForProperty = function (params) {
    var countryId = params.countryId, zip = params.zip, epcIsRequired = params.epcIsRequired;
    // By default use all available EPC-labels for non-Belgian properties
    var epcLabels = Object.values(EPropertyEPCLabel);
    if (countryId && zip) {
        if (countryId === "BE") {
            if (isFlemishProperty({ countryId: countryId, zip: zip })) {
                epcLabels = __spreadArray(__spreadArray([], (epcIsRequired
                    ? []
                    : [EPropertyEPCLabel.Unknown, EPropertyEPCLabel.NoEPCAvailable]), true), [
                    EPropertyEPCLabel.APlus,
                    EPropertyEPCLabel.A,
                    EPropertyEPCLabel.B,
                    EPropertyEPCLabel.C,
                    EPropertyEPCLabel.D,
                    EPropertyEPCLabel.E,
                    EPropertyEPCLabel.F,
                ], false);
            }
            else {
                // Brussels & wallonia have the same EPC-labels
                epcLabels = __spreadArray(__spreadArray([], (epcIsRequired
                    ? []
                    : [EPropertyEPCLabel.Unknown, EPropertyEPCLabel.NoEPCAvailable]), true), [
                    EPropertyEPCLabel.APlus,
                    EPropertyEPCLabel.A,
                    EPropertyEPCLabel.B,
                    EPropertyEPCLabel.C,
                    EPropertyEPCLabel.D,
                    EPropertyEPCLabel.E,
                    EPropertyEPCLabel.F,
                    EPropertyEPCLabel.G,
                ], false);
            }
        }
    }
    return Object.values(epcLabels).map(function (type) { return ({
        id: type,
        value: getLocalizedText("epc.label.".concat(type.toLowerCase())),
    }); });
};
export var getOwners = function (property) {
    return property.members
        .filter(function (member) { return member.roles.includes(EPropertyMemberTypes.Owner); })
        .map(function (member) { return member.account; });
};
export var hasBrusselsRegistration = function (property, leaseType) {
    return isBrusselsProperty(property) &&
        [ELeaseType.MainResidence, ELeaseType.StudentHousing].includes(leaseType);
};
export var getActivePropertiesFilterForApp = function () {
    return {
        contracts: {
            and: [
                {
                    startDate: { lte: NOW },
                },
                {
                    stopDate: { gte: NOW },
                },
            ],
        },
        archivedAt: { is: null },
    };
};
